package com.speedsoftware.nibbler.qr

import kotlin.math.abs
import kotlin.math.max

/**
 * A small QR code generator (ISO/IEC 18004): byte mode, error correction level M, versions 1-10, which is
 * plenty for an otpauth:// URI. Based on the structure of Project Nayuki's reference implementation.
 */
class QrCode private constructor(val version: Int, private val modules: Array<BooleanArray>) {
    val size: Int = modules.size

    /** True for a dark module; coordinates outside the symbol are light. */
    fun isDark(x: Int, y: Int): Boolean = x in 0 until size && y in 0 until size && modules[y][x]

    companion object {
        private const val MAX_VERSION = 10
        private val ECC_PER_BLOCK_M = intArrayOf(10, 16, 26, 18, 24, 16, 18, 22, 22, 26)
        private val NUM_BLOCKS_M = intArrayOf(1, 1, 1, 2, 2, 4, 4, 4, 5, 5)

        /** Encodes [text] as UTF-8; throws if it does not fit into version 10 at level M (213 bytes). */
        fun encodeText(text: String): QrCode {
            val bytes = text.encodeToByteArray()
            var version = 1
            while (version <= MAX_VERSION) {
                val countBits = if (version < 10) 8 else 16
                if (4 + countBits + bytes.size * 8 <= dataCodewords(version) * 8) break
                version++
            }
            require(version <= MAX_VERSION) { "Text too long for a QR code (${bytes.size} bytes)" }

            val data = buildDataCodewords(bytes, version)
            val allCodewords = addEccAndInterleave(data, version)
            return Builder(version).build(allCodewords)
        }

        private fun rawDataModules(ver: Int): Int {
            var result = (16 * ver + 128) * ver + 64
            if (ver >= 2) {
                val numAlign = ver / 7 + 2
                result -= (25 * numAlign - 10) * numAlign - 55
                if (ver >= 7) result -= 36
            }
            return result
        }

        private fun dataCodewords(ver: Int): Int =
            rawDataModules(ver) / 8 - ECC_PER_BLOCK_M[ver - 1] * NUM_BLOCKS_M[ver - 1]

        private fun buildDataCodewords(bytes: ByteArray, version: Int): ByteArray {
            val bits = ArrayList<Boolean>()
            fun append(value: Int, count: Int) { for (i in count - 1 downTo 0) bits.add(((value ushr i) and 1) == 1) }
            append(0b0100, 4) // byte mode
            append(bytes.size, if (version < 10) 8 else 16)
            for (b in bytes) append(b.toInt() and 0xff, 8)

            val capacityBits = dataCodewords(version) * 8
            append(0, minOf(4, capacityBits - bits.size)) // terminator
            while (bits.size % 8 != 0) bits.add(false)
            var pad = 0xEC
            while (bits.size < capacityBits) { append(pad, 8); pad = pad xor (0xEC xor 0x11) }

            val out = ByteArray(bits.size / 8)
            for (i in bits.indices) if (bits[i]) out[i ushr 3] = (out[i ushr 3].toInt() or (1 shl (7 - (i and 7)))).toByte()
            return out
        }

        private fun addEccAndInterleave(data: ByteArray, ver: Int): ByteArray {
            val numBlocks = NUM_BLOCKS_M[ver - 1]
            val blockEccLen = ECC_PER_BLOCK_M[ver - 1]
            val rawCodewords = rawDataModules(ver) / 8
            val numShortBlocks = numBlocks - rawCodewords % numBlocks
            val shortBlockLen = rawCodewords / numBlocks

            val blocks = ArrayList<ByteArray>()
            val divisor = reedSolomonDivisor(blockEccLen)
            var k = 0
            for (i in 0 until numBlocks) {
                val dat = data.copyOfRange(k, k + shortBlockLen - blockEccLen + (if (i < numShortBlocks) 0 else 1))
                k += dat.size
                val block = dat.copyOf(shortBlockLen + 1)
                val ecc = reedSolomonRemainder(dat, divisor)
                ecc.copyInto(block, block.size - blockEccLen)
                blocks.add(block)
            }

            val result = ByteArray(rawCodewords)
            var r = 0
            for (i in 0 until blocks[0].size) {
                for (j in blocks.indices) {
                    if (i != shortBlockLen - blockEccLen || j >= numShortBlocks) result[r++] = blocks[j][i]
                }
            }
            return result
        }

        private fun reedSolomonDivisor(degree: Int): ByteArray {
            val result = ByteArray(degree)
            result[degree - 1] = 1
            var root = 1
            repeat(degree) {
                for (j in result.indices) {
                    result[j] = gfMultiply(result[j].toInt() and 0xff, root).toByte()
                    if (j + 1 < result.size) result[j] = (result[j].toInt() xor result[j + 1].toInt()).toByte()
                }
                root = gfMultiply(root, 0x02)
            }
            return result
        }

        private fun reedSolomonRemainder(data: ByteArray, divisor: ByteArray): ByteArray {
            val result = ByteArray(divisor.size)
            for (b in data) {
                val factor = (b.toInt() xor result[0].toInt()) and 0xff
                result.copyInto(result, 0, 1, result.size)
                result[result.size - 1] = 0
                for (i in result.indices) {
                    result[i] = (result[i].toInt() xor gfMultiply(divisor[i].toInt() and 0xff, factor)).toByte()
                }
            }
            return result
        }

        private fun gfMultiply(x: Int, y: Int): Int {
            var z = 0
            for (i in 7 downTo 0) {
                z = (z shl 1) xor ((z ushr 7) * 0x11D)
                z = z xor (((y ushr i) and 1) * x)
            }
            return z
        }
    }

    private class Builder(val version: Int) {
        val size = version * 4 + 17
        val modules = Array(size) { BooleanArray(size) }
        val isFunction = Array(size) { BooleanArray(size) }

        fun build(codewords: ByteArray): QrCode {
            drawFunctionPatterns()
            drawCodewords(codewords)

            var bestMask = 0
            var bestPenalty = Int.MAX_VALUE
            for (mask in 0 until 8) {
                applyMask(mask)
                drawFormatBits(mask)
                val penalty = penaltyScore()
                if (penalty < bestPenalty) { bestPenalty = penalty; bestMask = mask }
                applyMask(mask) // undo
            }
            applyMask(bestMask)
            drawFormatBits(bestMask)
            return QrCode(version, modules)
        }

        private fun setFunction(x: Int, y: Int, dark: Boolean) {
            modules[y][x] = dark
            isFunction[y][x] = true
        }

        private fun bit(value: Int, i: Int) = ((value ushr i) and 1) != 0

        private fun drawFunctionPatterns() {
            for (i in 0 until size) {
                setFunction(6, i, i % 2 == 0)
                setFunction(i, 6, i % 2 == 0)
            }
            drawFinder(3, 3); drawFinder(size - 4, 3); drawFinder(3, size - 4)

            val positions = alignmentPositions()
            val n = positions.size
            for (i in 0 until n) for (j in 0 until n) {
                if ((i == 0 && j == 0) || (i == 0 && j == n - 1) || (i == n - 1 && j == 0)) continue
                drawAlignment(positions[i], positions[j])
            }
            drawFormatBits(0)
            drawVersion()
        }

        private fun alignmentPositions(): IntArray {
            if (version == 1) return IntArray(0)
            val numAlign = version / 7 + 2
            val step = if (version == 32) 26 else (version * 4 + numAlign * 2 + 1) / (numAlign * 2 - 2) * 2
            val result = IntArray(numAlign)
            result[0] = 6
            var pos = size - 7
            for (i in numAlign - 1 downTo 1) { result[i] = pos; pos -= step }
            return result
        }

        private fun drawFinder(cx: Int, cy: Int) {
            for (dy in -4..4) for (dx in -4..4) {
                val dist = max(abs(dx), abs(dy))
                val x = cx + dx
                val y = cy + dy
                if (x in 0 until size && y in 0 until size) setFunction(x, y, dist != 2 && dist != 4)
            }
        }

        private fun drawAlignment(cx: Int, cy: Int) {
            for (dy in -2..2) for (dx in -2..2) setFunction(cx + dx, cy + dy, max(abs(dx), abs(dy)) != 1)
        }

        private fun drawFormatBits(mask: Int) {
            val data = (0 shl 3) or mask // error correction level M is 00
            var rem = data
            repeat(10) { rem = (rem shl 1) xor ((rem ushr 9) * 0x537) }
            val bits = ((data shl 10) or rem) xor 0x5412

            for (i in 0..5) setFunction(8, i, bit(bits, i))
            setFunction(8, 7, bit(bits, 6))
            setFunction(8, 8, bit(bits, 7))
            setFunction(7, 8, bit(bits, 8))
            for (i in 9..14) setFunction(14 - i, 8, bit(bits, i))

            for (i in 0..7) setFunction(size - 1 - i, 8, bit(bits, i))
            for (i in 8..14) setFunction(8, size - 15 + i, bit(bits, i))
            setFunction(8, size - 8, true) // always dark
        }

        private fun drawVersion() {
            if (version < 7) return
            var rem = version
            repeat(12) { rem = (rem shl 1) xor ((rem ushr 11) * 0x1F25) }
            val bits = (version shl 12) or rem
            for (i in 0 until 18) {
                val dark = bit(bits, i)
                val a = size - 11 + i % 3
                val b = i / 3
                setFunction(a, b, dark)
                setFunction(b, a, dark)
            }
        }

        private fun drawCodewords(data: ByteArray) {
            var i = 0
            var right = size - 1
            while (right >= 1) {
                if (right == 6) right = 5
                for (vert in 0 until size) {
                    for (j in 0..1) {
                        val x = right - j
                        val upward = ((right + 1) and 2) == 0
                        val y = if (upward) size - 1 - vert else vert
                        if (!isFunction[y][x] && i < data.size * 8) {
                            modules[y][x] = bit(data[i ushr 3].toInt(), 7 - (i and 7))
                            i++
                        }
                    }
                }
                right -= 2
            }
        }

        private fun applyMask(mask: Int) {
            for (y in 0 until size) for (x in 0 until size) {
                val invert = when (mask) {
                    0 -> (x + y) % 2 == 0
                    1 -> y % 2 == 0
                    2 -> x % 3 == 0
                    3 -> (x + y) % 3 == 0
                    4 -> (x / 3 + y / 2) % 2 == 0
                    5 -> x * y % 2 + x * y % 3 == 0
                    6 -> (x * y % 2 + x * y % 3) % 2 == 0
                    else -> ((x + y) % 2 + x * y % 3) % 2 == 0
                }
                if (invert && !isFunction[y][x]) modules[y][x] = !modules[y][x]
            }
        }

        private fun penaltyScore(): Int {
            var result = 0
            for (y in 0 until size) {
                var runColor = false
                var runX = 0
                val history = IntArray(7)
                for (x in 0 until size) {
                    if (modules[y][x] == runColor) {
                        runX++
                        if (runX == 5) result += 3 else if (runX > 5) result++
                    } else {
                        addHistory(runX, history)
                        if (!runColor) result += countPatterns(history) * 40
                        runColor = modules[y][x]
                        runX = 1
                    }
                }
                result += terminateAndCount(runColor, runX, history) * 40
            }
            for (x in 0 until size) {
                var runColor = false
                var runY = 0
                val history = IntArray(7)
                for (y in 0 until size) {
                    if (modules[y][x] == runColor) {
                        runY++
                        if (runY == 5) result += 3 else if (runY > 5) result++
                    } else {
                        addHistory(runY, history)
                        if (!runColor) result += countPatterns(history) * 40
                        runColor = modules[y][x]
                        runY = 1
                    }
                }
                result += terminateAndCount(runColor, runY, history) * 40
            }
            for (y in 0 until size - 1) for (x in 0 until size - 1) {
                val c = modules[y][x]
                if (c == modules[y][x + 1] && c == modules[y + 1][x] && c == modules[y + 1][x + 1]) result += 3
            }
            var dark = 0
            for (row in modules) for (m in row) if (m) dark++
            val total = size * size
            val k = (abs(dark * 20 - total * 10) + total - 1) / total - 1
            return result + k * 10
        }

        private fun countPatterns(h: IntArray): Int {
            val n = h[1]
            val core = n > 0 && h[2] == n && h[3] == n * 3 && h[4] == n && h[5] == n
            return (if (core && h[0] >= n * 4 && h[6] >= n) 1 else 0) + (if (core && h[6] >= n * 4 && h[0] >= n) 1 else 0)
        }

        private fun terminateAndCount(currentRunColor: Boolean, currentRunLength: Int, h: IntArray): Int {
            var length = currentRunLength
            if (currentRunColor) { addHistory(length, h); length = 0 }
            length += size
            addHistory(length, h)
            return countPatterns(h)
        }

        private fun addHistory(currentRunLength: Int, h: IntArray) {
            var length = currentRunLength
            if (h[0] == 0) length += size
            h.copyInto(h, 1, 0, h.size - 1)
            h[0] = length
        }
    }
}
